import React, { useState, useEffect, useMemo } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Calendar.css';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../utils/config';
import Navbar from "../components/Navbar";

// Ensure MySQL datetime strings (no 'Z') are parsed as UTC
const parseUTC = (dateStr) => {
  if (!dateStr) return new Date();
  return new Date(String(dateStr).trim().replace(' ', 'T'));
};


const CalendarView = ({ hideNavbar = false, onNavigateToRooms }) => {
  const navigate = useNavigate();
  const [interviews, setInterviews] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(true);
  const [copiedLinkKey, setCopiedLinkKey] = useState('');

  const isSameDay = (a, b) => a.toDateString() === b.toDateString();

  const formatPanelDate = (date) =>
    date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });

  useEffect(() => {
    // Fetch scheduled interviews for the logged-in user from API
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;

    if (!userId) {
      console.warn('User id missing - cannot fetch interviews');
      return;
    }

    const fetchInterviews = async () => {
      try {
        const response = await fetch(
          `${API_BASE}/api/auth/interview-rooms/user/${userId}`
        );
        if (!response.ok) throw new Error('Failed to fetch interviews');
        const data = await response.json();
        console.log("Calendar View Fetched Interviews:", data);

        setInterviews(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching interviews:', error);
        setInterviews([]);
      }
    };

    fetchInterviews();
  }, []); // run once on mount

  const interviewsOnDate = useMemo(
    () =>
      Array.isArray(interviews) && selectedDate
        ? interviews.filter(
            (i) =>
              parseUTC(i.scheduled_date).toDateString() ===
              selectedDate.toDateString()
          )
        : [],
    [interviews, selectedDate]
  );

  const hasInterviewForDate = (date) =>
    Array.isArray(interviews) &&
    interviews.some(
      (i) =>
        parseUTC(i.scheduled_date).toDateString() === date.toDateString()
    );

  const getInterviewCountForDate = (date) =>
    Array.isArray(interviews)
      ? interviews.filter(
          (i) =>
            parseUTC(i.scheduled_date).toDateString() === date.toDateString()
        ).length
      : 0;

  const handleViewResult = (roomId) => {
    navigate(`/results/${roomId}`);
  };

  const handleGoToActiveRooms = () => {
    if (onNavigateToRooms) {
      onNavigateToRooms();
    } else {
      navigate('/hr-dashboard/interviews?tab=rooms');
    }
  };

  const copyInterviewLink = async (link, key) => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLinkKey(key);
      setTimeout(() => setCopiedLinkKey(''), 1400);
    } catch (e) {
      console.error('Failed to copy interview link', e);
    }
  };

  const filteredInterviews = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return interviewsOnDate;

    return interviewsOnDate.filter((i) => {
      const candidate = String(i.candidate_name || '').toLowerCase();
      const roomId = String(i.room_id || '').toLowerCase();
      return candidate.includes(q) || roomId.includes(q);
    });
  }, [interviewsOnDate, searchQuery]);

  const groupedInterviews = useMemo(() => {
    const morning = [];
    const afternoon = [];
    filteredInterviews.forEach((i) => {
      const hour = parseUTC(i.scheduled_date).getHours();
      if (hour < 12) {
        morning.push(i);
      } else {
        afternoon.push(i);
      }
    });
    return { morning, afternoon };
  }, [filteredInterviews]);

  const headerSubtitle = formatPanelDate(selectedDate);

  const renderInterviewCard = (i) => (
    <div
      key={i.room_id}
      className={`interview-card interview-card--${String(i.status || '').toLowerCase()}`}
    >
      <div className="card-top-row">
        <div className="card-left">
          <div className="interview-time">
            <span className="time-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
                <path d="M12 8v4l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <strong>
              {parseUTC(i.scheduled_date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </strong>
          </div>
          <span className={`status-chip status-chip--${String(i.status || '').toLowerCase()}`}>{i.status}</span>
        </div>

        <div className="interview-id">
          <span>Meeting ID:</span>
          <strong>{i.room_id}</strong>
        </div>
      </div>

      <div className="card-center">
        <div className="candidate-row">
          <span className="candidate-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M20 21a8 8 0 10-16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
            </svg>
          </span>
          <div className="candidate-text">
            <span className="candidate-label">Candidate</span>
            <strong className="candidate-name">{i.candidate_name || 'N/A'}</strong>
          </div>
        </div>
        <div className="type-row">
          Type: {i.interview_type === 'AI' ? 'Autonomous AI' : 'Personal (HR)'}
        </div>
      </div>

      <div className="card-right">
        <div className="interview-actions">
          {i.status === 'SCHEDULED' && (
            <>
              <div className="link-action-row">
                <a
                  href={i.candidate_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="join-link join-link-primary join-link-with-copy"
                  onClick={(e) => {
                    if (e.target.closest('.join-link-copy-icon')) {
                      e.preventDefault();
                      copyInterviewLink(i.candidate_link, `${i.room_id}-candidate`);
                    }
                  }}
                >
                  <span className="join-link-label">
                    <span className="btn-icon" aria-hidden="true">🎥</span>
                    Join as Candidate
                  </span>
                  <span
                    className={`join-link-copy-icon ${copiedLinkKey === `${i.room_id}-candidate` ? 'copied' : ''}`}
                    aria-hidden="true"
                    title={copiedLinkKey === `${i.room_id}-candidate` ? 'Copied' : 'Copy candidate link'}
                  >
                    {copiedLinkKey === `${i.room_id}-candidate` ? (
                      <svg viewBox="0 0 24 24" fill="none">
                        <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none">
                        <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                        <path d="M15 9V7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    )}
                  </span>
                  {copiedLinkKey === `${i.room_id}-candidate` && (
                    <span className="join-link-copy-toast" aria-live="polite">Link copied</span>
                  )}
                </a>
              </div>

              {i.interviewer_link && (
                <div className="link-action-row">
                  <a
                    href={i.interviewer_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="join-link join-link-secondary join-link-with-copy"
                    onClick={(e) => {
                      if (e.target.closest('.join-link-copy-icon')) {
                        e.preventDefault();
                        copyInterviewLink(i.interviewer_link, `${i.room_id}-interviewer`);
                      }
                    }}
                  >
                    <span className="join-link-label">
                      <span className="btn-icon" aria-hidden="true">🎥</span>
                      Join as Interviewer
                    </span>
                    <span
                      className={`join-link-copy-icon ${copiedLinkKey === `${i.room_id}-interviewer` ? 'copied' : ''}`}
                      aria-hidden="true"
                      title={copiedLinkKey === `${i.room_id}-interviewer` ? 'Copied' : 'Copy interviewer link'}
                    >
                      {copiedLinkKey === `${i.room_id}-interviewer` ? (
                        <svg viewBox="0 0 24 24" fill="none">
                          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none">
                          <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                          <path d="M15 9V7a2 2 0 00-2-2H6a2 2 0 00-2 2v7a2 2 0 002 2h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      )}
                    </span>
                    {copiedLinkKey === `${i.room_id}-interviewer` && (
                      <span className="join-link-copy-toast" aria-live="polite">Link copied</span>
                    )}
                  </a>
                </div>
              )}
            </>
          )}

          {i.status === 'COMPLETED' && (
            <button
              className="view-result-btn"
              onClick={() => handleViewResult(i.room_id)}
            >
              View result
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`page-center-wrapper ${hideNavbar ? 'embedded-mode' : ''}`}>
      {!hideNavbar && <Navbar isLoggedIn userName={JSON.parse(localStorage.getItem('user') || '{}')?.full_name} />}
      <div className="calendar-main-area">
        <div className="view-calendar-layout">
          <div className="view-calendar-wrapper">
            <div className="calendar-card-topbar">
              <div className="calendar-card-head">
                <span className="calendar-head-label">Interview Calendar</span>
                {isSameDay(selectedDate, new Date()) && (
                  <span className="today-badge">Today</span>
                )}
              </div>
              <button
                type="button"
                className="mobile-calendar-toggle"
                onClick={() => setMobileCalendarOpen((prev) => !prev)}
              >
                {mobileCalendarOpen ? 'Hide Calendar' : 'Show Calendar'}
              </button>
            </div>

            <div className={`calendar-shell ${mobileCalendarOpen ? 'open' : 'collapsed'}`}>
              <Calendar
                onChange={setSelectedDate}
                value={selectedDate}
                className="view-calendar"
                calendarType="gregory"
                tileClassName={({ date, view }) => {
                  if (view === 'month' && hasInterviewForDate(date)) {
                    return 'has-interview';
                  }
                  return null;
                }}
                tileContent={({ date, view }) => {
                  if (view !== 'month') return null;
                  const count = getInterviewCountForDate(date);
                  if (!count) return null;
                  return (
                    <span
                      className="calendar-event-dot"
                      title={`${count} interview${count > 1 ? 's' : ''}`}
                    />
                  );
                }}
              />
            </div>
          </div>
          {selectedDate && (
            <div className="view-calendar-sidepanel">
              <div className="sidepanel-header-row">
                <div className="sidepanel-header">
                  <h3>Interview Schedule</h3>
                  <p>{headerSubtitle}</p>
                </div>
                <button className="dashboard-cta-btn" onClick={handleGoToActiveRooms}>
                  Active Rooms
                </button>
              </div>

              <div className="panel-controls">
                <div className="search-wrap">
                  <svg viewBox="0 0 24 24" fill="none" className="search-icon" aria-hidden="true">
                    <path d="M11 19a8 8 0 100-16 8 8 0 000 16z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search candidate or meeting ID"
                    className="schedule-search"
                  />
                </div>
              </div>

              <div className="interview-list" role="region" aria-label="Scheduled interviews list">
                {filteredInterviews.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-illustration" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="5" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M8 3v4M16 3v4M3 10h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </div>
                    <p className="empty-text">No interviews scheduled</p>
                  </div>
                ) : (
                  <>
                    {groupedInterviews.morning.length > 0 && (
                      <div className="group-block">
                        <h4 className="group-heading">Morning</h4>
                        {groupedInterviews.morning.map((i) => renderInterviewCard(i))}
                      </div>
                    )}

                    {groupedInterviews.afternoon.length > 0 && (
                      <div className="group-block">
                        <h4 className="group-heading">Afternoon</h4>
                        {groupedInterviews.afternoon.map((i) => renderInterviewCard(i))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
